import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Newsletter } from "@/components/site/Newsletter";
import { MaskLines, Reveal } from "@/components/site/motion";
import { LINKS, LOYALTY, TOAST_PROMO, NEW_ITEMS, MERCH } from "@/content/site";

const GiftCard3D = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 120, damping: 14 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div style={{ perspective: 1200 }} onPointerMove={move} onPointerLeave={() => { mx.set(0); my.set(0); }} className="mx-auto w-full max-w-[460px]">
      <motion.div data-testid="gift-card-visual" style={{ rotateX: rx, rotateY: ry }} className="grain relative aspect-[1.6/1] overflow-hidden rounded-[1.8rem] bg-[#1a0a10] p-7 text-[#fff5e8] shadow-[0_50px_80px_-30px_rgba(0,0,0,0.6)]">
        <div className="absolute -right-10 -top-16 h-64 w-64 rounded-full bg-[#ff2e63]" />
        <div className="absolute -bottom-20 right-16 h-48 w-48 rounded-full bg-[#ffd23f]" />
        <img src="/img/cup-strawberry.webp" alt="" className="absolute -bottom-6 right-6 h-[110%] w-auto drop-shadow-2xl" />
        <div className="relative flex h-full flex-col justify-between">
          <img src="/img/logo.webp" alt="TeaDM" className="h-14 w-14 rounded-full" />
          <div><p className="eyebrow text-[#ffd23f]">Gift card</p><p className="font-display text-4xl font-extrabold leading-none">TeaDM</p></div>
        </div>
      </motion.div>
    </div>
  );
};

export default function GiftCards() {
  return (
    <>
      <Seo title="TeaDM Gift Cards, Rewards & Merch — Bubble Tea Gifts" description="Buy TeaDM gift cards valid at all locations, earn a free drink for every $50 spent with TeaDM Rewards, and see what's new at TeaDM in Northern Virginia." path="/gift-cards" />
      <PageHero testId="gift-hero" bg="#ffd23f" fg="#1a0a10" eyebrow="Gift cards & rewards" lines={["Treat", "someone."]} intro="Gift cards are valid at all locations. Every $50 you spend earns a free drink.">
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={LINKS.giftCards} target="_blank" rel="noopener noreferrer" data-testid="gift-buy-btn" className="btn btn-ink">Buy a gift card <ArrowUpRight className="h-4 w-4" /></a>
          <a href={LINKS.rewards} target="_blank" rel="noopener noreferrer" data-testid="gift-rewards-btn" className="btn btn-ghost"><Star className="h-4 w-4" />Rewards lookup</a>
        </div>
      </PageHero>

      <section className="bg-[#fff5e8] py-24 sm:py-32">
        <div className="wrap grid items-center gap-14 lg:grid-cols-2">
          <GiftCard3D />
          <div>
            <p className="eyebrow text-[#ff2e63]">TeaDM Rewards</p>
            <div className="mt-6 grid grid-cols-3 gap-3 text-center" data-testid="loyalty-equation">
              {[[`$${LOYALTY.spend}`, "spent"], [LOYALTY.points, "points"], ["1", "free drink"]].map(([n, l], i) => (
                <Reveal key={l} delay={i * 0.1} className="rounded-[1.6rem] bg-white p-5">
                  <span className="block font-display text-4xl font-extrabold text-[#ff2e63] sm:text-5xl">{n}</span>
                  <span className="eyebrow mt-2 block">{l}</span>
                </Reveal>
              ))}
            </div>
            <p className="mt-6 text-lg">Every $50 spent = 50 points = 1 free drink. Join or check your points on Toast.</p>
            <a href={LINKS.rewards} target="_blank" rel="noopener noreferrer" data-testid="gift-loyalty-join-btn" className="btn btn-berry mt-8">Join our loyalty program <ArrowUpRight className="h-4 w-4" /></a>
          </div>
        </div>
      </section>

      <section className="bg-[#ff2e63] py-20 text-[#fff5e8]">
        <div className="wrap flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <p className="eyebrow">Current online promo</p>
            <p className="mt-3 font-display text-5xl font-extrabold uppercase leading-none sm:text-7xl">{TOAST_PROMO.title}</p>
            <p className="mt-4 max-w-xl">Use code <strong className="rounded-md bg-[#1a0a10] px-2 py-0.5 font-display">{TOAST_PROMO.code}</strong>. {TOAST_PROMO.note}</p>
          </div>
          <a href={LINKS.toastHub} target="_blank" rel="noopener noreferrer" data-testid="gift-promo-order-btn" className="btn btn-cream self-start">Order on Toast <ArrowUpRight className="h-4 w-4" /></a>
        </div>
      </section>

      <section className="bg-[#fff5e8] py-24 sm:py-32">
        <div className="wrap">
          <p className="eyebrow text-[#ff2e63]">Just landed</p>
          <MaskLines lines={["New at", "Falls Church."]} className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl" />
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {NEW_ITEMS.map((n, i) => (
              <Reveal key={n.name} delay={i * 0.1} className="group overflow-hidden rounded-[2rem] bg-white">
                <div className="aspect-[4/5] overflow-hidden sm:aspect-[4/3]"><img src={n.image} alt={n.name} loading="lazy" className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-6"><h3 className="font-display text-2xl font-extrabold">{n.name}</h3><p className="mt-2 text-[#1a0a10]/80">{n.note} Available at {n.where}.</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1a0a10] py-24 text-[#fff5e8] sm:py-32">
        <div className="wrap">
          <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <p className="eyebrow text-[#ffd23f]">Merch</p>
              <h2 className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-7xl">Wear the beat.</h2>
            </div>
            <p className="max-w-sm text-[#fff5e8]/80">Our hoodie and tees are currently sold out — follow us on Instagram for restocks.</p>
          </div>
          <div className="mt-12 grid gap-4 sm:grid-cols-3">
            {MERCH.map((m) => (
              <div key={m.name} data-testid={`merch-${m.name.toLowerCase().replace(/\s+/g, "-")}`} className="relative rounded-[2rem] bg-[#efe4d6] p-6 text-[#1a0a10]">
                <span className="absolute left-5 top-5 rounded-full bg-[#1a0a10] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#fff5e8]">Sold out</span>
                <img src={m.image} alt={m.name} loading="lazy" className="mx-auto aspect-square w-full object-contain" />
                <p className="mt-4 font-display text-xl font-bold">{m.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#ffd23f] py-20">
        <div className="wrap grid items-center gap-8 lg:grid-cols-2">
          <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-6xl">Get the drop first.</h2>
          <div>
            <Newsletter source="gift-cards" dark={false} testId="gift-newsletter" />
            <p className="mt-3 text-sm">Or <a href={LINKS.toastNewsletter} target="_blank" rel="noopener noreferrer" className="font-bold underline" data-testid="gift-toast-newsletter">subscribe through Toast</a> for ordering offers.</p>
          </div>
        </div>
      </section>
    </>
  );
}
